import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { motion, useReducedMotion } from 'framer-motion';
import { Spin } from 'antd';
import { fluid } from '../utils/responsive';
import { COLORS, MONTH_NAMES } from '../utils/constants';
import { buildLinePath, buildAreaPath, EASE_OUT, CARD_DURATION } from '../utils/animations';
import { getStudentStats } from 'actions/studentManagement';
import ArrowSquareLeft from '../../../pages/userManagementV2/dashboardV3/arrow-square-left.svg';
import ArrowSquareRight from '../../../pages/userManagementV2/dashboardV3/arrow-square-right.svg';

const Y_AXIS_STEPS = [600, 450, 300, 150, 0];
const CHART_WIDTH = 900;
const CHART_HEIGHT = 220;
// Horizontal inset (chart units) so the first/last points — and their count
// tooltip — sit in the middle of the Jan/Dec labels instead of on the edge.
const CHART_X_PADDING = 30;

const ActiveStudentsCard = React.memo(({ isMobile = false, getStudentStats, onYearLoading, refreshTrigger }) => {
    const reduceMotion = useReducedMotion();
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(currentYear);
    // Zeroed-out placeholder for the requested year's 12 months, shown until
    // the /userv2/student/stats response for that year lands.
    const [trend, setTrend] = useState(() => MONTH_NAMES.map((month) => ({ label: `${month} ${currentYear}`, count: 0 })));
    const [loading, setLoading] = useState(false);
    const isFirstMount = useRef(true);
    // Year whose line-draw animation has already played — a later re-mount of
    // the path for the same year (data refresh) shows the line without
    // replaying the draw.
    const drawnYearRef = useRef(null);
    const [hoveredIndex, setHoveredIndex] = useState(null);

    // GET /userv2/student/stats?range=all&year=<year> — refetched whenever
    // the prev/next year arrows change `year`.
    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            // One call for the page: year only. Stored in redux (no skipRedux) so the
            // Students distribution card uses the same response for its counts.
            getStudentStats?.({ year: String(year) }, (response) => {
                const payload = response?.data ?? response;
                const activeStudentsTrend = payload?.activeStudentsTrend;
                if (Array.isArray(activeStudentsTrend) && activeStudentsTrend.length > 0) {
                    setTrend(activeStudentsTrend);
                }
            });
            return;
        }

        setLoading(true);
        onYearLoading?.(true);
        const safety = setTimeout(() => {
            setLoading(false);
            onYearLoading?.(false);
        }, 5000);

        let isCurrent = true;
        getStudentStats?.({ year: String(year) }, (response, error) => {
            if (!isCurrent) return;
            clearTimeout(safety);
            if (error || !response) {
                setLoading(false);
                onYearLoading?.(false);
                return;
            }
            const payload = response?.data ?? response;
            const activeStudentsTrend = payload?.activeStudentsTrend;
            if (Array.isArray(activeStudentsTrend) && activeStudentsTrend.length > 0) {
                setTrend(activeStudentsTrend);
            } else {
                setTrend(MONTH_NAMES.map((month) => ({ label: `${month} ${year}`, count: 0 })));
            }
            setLoading(false);
            onYearLoading?.(false);
        });

        return () => {
            isCurrent = false;
            clearTimeout(safety);
        };
    }, [year, getStudentStats, onYearLoading]);

    // Re-fetch active student trend when student status changes — only when
    // refreshTrigger actually changes (it also ran on first load before).
    const prevRefreshTriggerRef = useRef(refreshTrigger);
    useEffect(() => {
        if (prevRefreshTriggerRef.current === refreshTrigger) return;
        prevRefreshTriggerRef.current = refreshTrigger;
        getStudentStats?.({ year: String(year) }, (response, error) => {
            if (error || !response) return;
            const payload = response?.data ?? response;
            const activeStudentsTrend = payload?.activeStudentsTrend;
            if (Array.isArray(activeStudentsTrend) && activeStudentsTrend.length > 0) {
                setTrend(activeStudentsTrend);
            }
        });
    }, [refreshTrigger, year, getStudentStats]);

    const data = useMemo(() => trend.map((t) => t.count || 0), [trend]);
    // Current year: draw only up to the current month; past years: all 12.
    const lastVisibleIndex = year === currentYear ? new Date().getMonth() : data.length - 1;
    const canGoPrev = !loading;
    const canGoNext = !loading && year < currentYear;

    const points = useMemo(() => data.map((value, i) => ({
        x: CHART_X_PADDING + (i / Math.max(data.length - 1, 1)) * (CHART_WIDTH - 2 * CHART_X_PADDING),
        y: CHART_HEIGHT - (value / 600) * CHART_HEIGHT,
    })), [data]);

    // Points (dots / count tooltip / month labels) are inset by
    // CHART_X_PADDING, but the line itself still runs edge to edge — extend
    // it flat from x=0 to the first point and from the last point to the
    // right edge.
    const visiblePoints = useMemo(() => points.slice(0, lastVisibleIndex + 1), [points, lastVisibleIndex]);

    const linePath = useMemo(() => {
        if (!visiblePoints.length) return buildLinePath(visiblePoints);
        const first = visiblePoints[0];
        const last = visiblePoints[visiblePoints.length - 1];
        // Only run on to the right edge when the whole year is shown.
        const tail = visiblePoints.length === points.length ? [{ x: CHART_WIDTH, y: last.y }] : [];
        return buildLinePath([{ x: 0, y: first.y }, ...visiblePoints, ...tail]);
    }, [points, visiblePoints]);

    return (
        <div style={{
            backgroundColor: "#FFFFFF",
            borderRadius: fluid(25),
            padding: isMobile ? "18px" : fluid(24),
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            fontFamily: "Roboto, sans-serif",
            position: "relative",
        }}>
            {/* Same in-card loader as ActiveStaffCard — only this graph card
                shows the loader on year change, not the whole page. */}
            {loading && (
                <div style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.75)",
                    zIndex: 20,
                    borderRadius: fluid(25),
                }}>
                    <Spin size="large" />
                </div>
            )}

            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
                gap: fluid(12),
                flexWrap: "wrap",
            }}>
                <span style={{ fontSize: fluid(24), fontWeight: 700, color: "#191919", fontFamily: "Roboto, sans-serif" }}>
                    Active students
                </span>
                <span style={{ fontSize: fluid(20), fontWeight: 700, color: "#191919", fontFamily: "Roboto, sans-serif" }}>
                    {year}
                </span>
            </div>

            <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: fluid(22),
                flexShrink: 0,
                gap: fluid(12),
                flexWrap: "wrap",
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: fluid(8) }}>
                    <span style={{
                        width: fluid(16),
                        height: fluid(16),
                        borderRadius: fluid(4),
                        backgroundColor: "#1982C4",
                        flexShrink: 0,
                    }} />
                    <span style={{ fontSize: "14px", fontWeight: 400, color: "#636363", fontFamily: "Roboto, sans-serif" }}>Students</span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: fluid(16) }}>
                    <button
                        type="button"
                        onClick={() => canGoPrev && setYear((y) => y - 1)}
                        aria-label="Previous year"
                        style={{
                            width: fluid(32),
                            height: fluid(32),
                            border: "none",
                            padding: 0,
                            background: "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: canGoPrev ? "pointer" : "not-allowed",
                            opacity: canGoPrev ? 1 : 0.35,
                        }}
                    >
                        <img src={ArrowSquareLeft} alt="" style={{ width: fluid(32), height: fluid(32) }} />
                    </button>
                    <button
                        type="button"
                        onClick={() => canGoNext && setYear((y) => y + 1)}
                        aria-label="Next year"
                        style={{
                            width: fluid(32),
                            height: fluid(32),
                            border: "none",
                            padding: 0,
                            background: "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: canGoNext ? "pointer" : "not-allowed",
                            opacity: canGoNext ? 1 : 0.35,
                        }}
                    >
                        <img src={ArrowSquareRight} alt="" style={{ width: fluid(32), height: fluid(32) }} />
                    </button>
                </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, marginTop: fluid(32) }}>
                <div style={{ display: "flex", flex: 1, minHeight: 0, gap: fluid(12) }}>
                    {/* Fixed Y axis steps */}
                    <div style={{ position: "relative", flexShrink: 0, width: fluid(32), height: fluid(CHART_HEIGHT) }}>
                        {Y_AXIS_STEPS.map((step) => (
                            <span
                                key={step}
                                style={{
                                    position: "absolute",
                                    top: `${((600 - step) / 600) * 100}%`,
                                    right: 0,
                                    transform: "translateY(-50%)",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    color: "#AEAEAE",
                                    whiteSpace: "nowrap",
                                    fontFamily: "Roboto, sans-serif",
                                }}
                            >
                                {step}
                            </span>
                        ))}
                    </div>

                    {/* Scrollable Graph viewport */}
                    <div
                        style={{
                            flex: 1,
                            overflowX: "auto",
                            overflowY: "hidden",
                            minHeight: 0,
                            position: "relative",
                            paddingBottom: fluid(8)
                        }}
                        className="dv2-chart-scroll-on-hover"
                    >
                        <div style={{ width: "240%", display: "flex", flexDirection: "column", height: "100%" }}>
                            <div style={{ position: "relative", width: "100%", height: fluid(CHART_HEIGHT), flexShrink: 0 }}>
                            <svg
                                viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
                                preserveAspectRatio="none"
                                style={{ width: "100%", height: fluid(CHART_HEIGHT), minHeight: 0, overflow: "visible" }}
                                onMouseLeave={() => setHoveredIndex(null)}
                            >
                                {Y_AXIS_STEPS.map((step) => {
                                    const y = CHART_HEIGHT - (step / 600) * CHART_HEIGHT;
                                    return (
                                        <line
                                            key={step}
                                            x1={0}
                                            x2={CHART_WIDTH}
                                            y1={y}
                                            y2={y}
                                            stroke="#E6E6E6"
                                            strokeWidth={2}
                                            strokeDasharray="10 10"
                                        />
                                    );
                                })}

                                {!loading && (
                                    <motion.path
                                        // Keyed by the path too: framer-motion measures the path length only on
                                        // mount, so a changed line must re-mount or its end gets cut off.
                                        key={`line-${year}-${linePath}`}
                                        d={linePath}
                                        fill="none"
                                        stroke="#1982C4"
                                        strokeWidth={2.5}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        initial={reduceMotion || drawnYearRef.current === year ? false : { pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 1, ease: EASE_OUT }}
                                        onAnimationComplete={() => { drawnYearRef.current = year; }}
                                    />
                                )}

                                {/* Larger invisible hit-targets (easier to hover than the
                                    bare line) plus a visible dot on whichever is hovered. */}
                                {!loading && visiblePoints.map((point, i) => (
                                    <g key={i}>
                                        <circle
                                            cx={point.x}
                                            cy={point.y}
                                            r={16}
                                            fill="transparent"
                                            onMouseEnter={() => setHoveredIndex(i)}
                                            style={{ cursor: "pointer" }}
                                        />
                                        {hoveredIndex === i && (
                                            <circle
                                                cx={point.x}
                                                cy={point.y}
                                                r={5}
                                                fill="#FFFFFF"
                                                stroke="#1982C4"
                                                strokeWidth={2}
                                                style={{ pointerEvents: "none" }}
                                            />
                                        )}
                                    </g>
                                ))}
                            </svg>

                            {!loading && hoveredIndex !== null && points[hoveredIndex] && (
                                <div
                                    style={{
                                        position: "absolute",
                                        left: `${(points[hoveredIndex].x / CHART_WIDTH) * 100}%`,
                                        top: `${(points[hoveredIndex].y / CHART_HEIGHT) * 100}%`,
                                        transform: "translate(-50%, calc(-100% - 12px))",
                                        backgroundColor: "#191919",
                                        color: "#FFFFFF",
                                        fontSize: "11px",
                                        fontWeight: 500,
                                        padding: "4px 8px",
                                        borderRadius: "8px",
                                        whiteSpace: "nowrap",
                                        zIndex: 3,
                                        pointerEvents: "none",
                                        fontFamily: "Roboto, sans-serif",
                                    }}
                                >
                                    {data[hoveredIndex]}
                                </div>
                            )}
                            </div>

                            {/* Month labels aligns with width of CHART_WIDTH */}
                            <div style={{ position: "relative", height: "20px", marginTop: fluid(8) }}>
                                {trend.map((t, i) => (
                                    <span
                                        key={t.label || i}
                                        style={{
                                            position: "absolute",
                                            left: `${((points[i]?.x ?? 0) / CHART_WIDTH) * 100}%`,
                                            transform: "translateX(-50%)",
                                            fontSize: "14px",
                                            fontWeight: 400,
                                            color: "#AEAEAE",
                                            textAlign: "center",
                                            whiteSpace: "nowrap",
                                            fontFamily: "Roboto, sans-serif",
                                        }}
                                    >
                                        {(t.label || "").split(" ")[0]}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

ActiveStudentsCard.displayName = 'ActiveStudentsCard';

const mapDispatchToProps = (dispatch) => ({
    getStudentStats: (params, callback) => dispatch(getStudentStats(params, callback)),
});

export default connect(null, mapDispatchToProps)(ActiveStudentsCard);
